import React from "react";
import DashboardKpiStatus from "components/DashboardKpiStatus";
import { Card, CardContent, Grid, Typography, Avatar } from "@mui/material";
import { TbCurrencyRupee } from "react-icons/tb";
const getIconBGColor = (planned, actual) => {
  if (actual >= planned) {
    return "#3CD856"
  }
  else {
    return "#FA5A7D"
  }
}

const ImportantKpis = ({ kpiData }) => {
  // First row from API
  const row = kpiData?.data?.[0];

  // Helper: format as ₹ xx.xx Cr (adjust if your numbers are already in Cr)
  const formatCrore = (value) => {
    if (value === null || value === undefined || isNaN(value) || value == 0) {
      return "";
    }
    const num = Number(value); // or divide by 10000000 if backend sends rupees
    return `₹ ${num.toFixed(2)} Cr`;
  };

  if (!row) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: "3px",
          border: "1px solid #eef0f4",
          p: 1.5,
          width: "100%",
          minHeight: "180px",
          boxSizing: "border-box",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 600,
            fontSize: "15px",
            lineHeight: "21px",
            letterSpacing: "0px",
            mb: 0,
          }}
        >
          Cost Intelligence
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 300,
            fontSize: "12px",
            lineHeight: "21px",
            letterSpacing: "0px",
            mb: 2,
          }}
        >
          Monitor approved value, forecast exposure, and earned-value performance.
        </Typography>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography
              sx={{
                fontFamily: "Outfit, sans-serif",
                fontSize: "14px",
                color: "#6B7280",
                mt: 2,
              }}
            >
              No cost KPI data available.
            </Typography>
          </Grid>
        </Grid>
      </Card>
    );
  }

  // Read baseline-related values (support both camelCase and snake/lower)
  const baselineCost =
    row.baselineCost ?? row.baselinecost ?? row.totalBaselineCost;
  const baselineHardCost =
    row.baselineHardCost ?? row.baselinehardcost ?? row.baselineHard;
  const baselineSoftCost =
    row.baselineSoftCost ?? row.baselinecosftcost ?? row.baselineSoft;

  // If total baseline is missing but hard + soft are present, compute it
  const totalBaselineValue =
    baselineCost ??
    (baselineHardCost != null && baselineSoftCost != null
      ? Number(baselineHardCost) + Number(baselineSoftCost)
      : null);

  const revisedBaselineCost =
    row.revisedbaselineCost ?? row.revisedbaselinecost ?? row.totalBaselineCost;
  const revisedBaselineHardCost =
    row.revisedbaselineHardCost ?? row.revisedbaselinehardcost ?? row.baselineHard;
  const revisedBaselineSoftCost =
    row.revisedbaselineSoftCost ?? row.revisedbaselinecosftcost ?? row.baselineSoft;

  // If total baseline is missing but hard + soft are present, compute it
  const totalRevisedBaselineValue =
    revisedBaselineCost ??
    (revisedBaselineHardCost != null && revisedBaselineSoftCost != null
      ? Number(revisedBaselineHardCost) + Number(revisedBaselineSoftCost)
      : null);

  // Other KPIs
  const otherKpis = [
    {
      label: "Estimate at Completion (EAC)",
      value: formatCrore(row.eac ?? row.estimateAtCompletion),
      bg: "#F3E8FF",
      iconBg: "#BF83FF",
    },
    {
      label: "Earned Value (EV)",
      value: formatCrore(row.earned ?? row.actual),
      bg: "#DCFCE7",
      iconBg: "#3CD856",
    },
    // {
    //   label: "Estimate to Complete (ETC)",
    //   value: formatCrore(row.etc ?? row.estimateToComplete),
    //   bg: "#FFE2E5",
    //   iconBg: "#FA5A7D",
    // },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "3px",
        border: "1px solid #eef0f4",
        p: 1.5,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontWeight: 600,
          fontSize: "15px",
          lineHeight: "21px",
          letterSpacing: "0px",
          mb: 0,
        }}
      >
        Cost Intelligence
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "Outfit, sans-serif",
          fontWeight: 300,
          fontSize: "12px",
          lineHeight: "21px",
          letterSpacing: "0px",
          mb: 2,
        }}
      >
        Monitor approved value, forecast exposure, and earned-value performance.
      </Typography>

      <Grid container spacing={1} sx={{ flex: 1, alignItems: "stretch" }}>
        {/* Baseline / Total Value card */}
        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0, display: "flex" }}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              minHeight: "124px",
              width: "100%",
              position: "relative",
              borderRadius: "3px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderBottom: "3px solid #FF947A",
            }}
          >
            <CardContent sx={{ gap: 1, p: 1.25 }}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: "#FF947A",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                <TbCurrencyRupee />
              </Avatar>
              <DashboardKpiStatus color="#FF947A" />

              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "21px",
                  letterSpacing: "0px",
                  color: "#151D48",
                }}
              >
                {formatCrore(totalBaselineValue)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0px",
                  color: "#425166",
                }}
              >
                Total Value (R0)
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 400,
                  mt: 0.25,
                  fontSize: "10px",
                  lineHeight: "13px",
                  color: "#667085",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Hard: {formatCrore(baselineHardCost)}
                {baselineSoftCost > 0 ? ` | Soft: ${formatCrore(baselineSoftCost)}` : ""}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} sx={{ minWidth: 0, display: "flex" }}>
          <Card
            elevation={0}
            sx={{
              height: "100%",
              minHeight: "124px",
              width: "100%",
              position: "relative",
              borderRadius: "3px",
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderBottom: `3px solid ${getIconBGColor(totalRevisedBaselineValue, totalBaselineValue)}`,
            }}
          >
            <CardContent sx={{ gap: 1, p: 1.25 }}>
              <Avatar
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: getIconBGColor(totalRevisedBaselineValue, totalBaselineValue),// "#FF947A",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                <TbCurrencyRupee />
              </Avatar>
              <DashboardKpiStatus color={getIconBGColor(totalRevisedBaselineValue, totalBaselineValue)} />

              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 600,
                  fontSize: "16px",
                  lineHeight: "21px",
                  letterSpacing: "0px",
                  color: "#151D48",
                }}
              >
                {formatCrore(totalRevisedBaselineValue)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 500,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0px",
                  color: "#425166",
                }}
              >
                Total Value (Revised)
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Outfit, sans-serif",
                  fontWeight: 400,
                  mt: 0.25,
                  fontSize: "10px",
                  lineHeight: "13px",
                  color: "#667085",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Hard: {formatCrore(revisedBaselineHardCost)}
                {revisedBaselineSoftCost > 0 ? ` | Soft: ${formatCrore(revisedBaselineSoftCost)}` : ""}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Other KPIs */}
        {otherKpis.map((item) => (
          <Grid size={{ xs: 12, sm: 6 }} key={item.label} sx={{ minWidth: 0, display: "flex" }}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                minHeight: "124px",
                width: "100%",
                position: "relative",
                borderRadius: "3px",
                backgroundColor: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderBottom: `3px solid ${item.iconBg}`,
              }}
            >
              <CardContent sx={{ gap: 1, p: 1.25 }}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    backgroundColor: item.iconBg,
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  <TbCurrencyRupee />
                </Avatar>
                <DashboardKpiStatus color={item.iconBg} />

                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "21px",
                    letterSpacing: "0px",
                    color: "#151D48",
                  }}
                >
                  {item.value}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Outfit, sans-serif",
                    fontWeight: 500,
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "0px",
                    color: "#425166",
                  }}
                >
                  {item.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

export default ImportantKpis;
